import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  useMediaQuery,
  Skeleton,
  InputAdornment,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';

const SearchById = ({ showOnlyFollowing = false }) => {
  const [resellers, setResellers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);
  const [followingIds, setFollowingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowIndices, setRowIndices] = useState({});
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const cardsPerRowVisible = 5;
  const totalRows = 3;

  useEffect(() => {
    const fetchResellers = async () => {
      try {
        setLoading(true);
        const allResellers = await fetch('https://reviewhub-lc.onrender.com/api/resellers/all').then((res) => res.json());

        const resellersWithFollowers = await Promise.all(
          allResellers.map(async (reseller) => {
            try {
              const response = await fetch(`https://reviewhub-lc.onrender.com/api/auth/reseller-followers/${reseller.resellerId}`);
              const data = await response.json();
              return { ...reseller, followerCount: data.followerCount || 0 };
            } catch {
              return { ...reseller, followerCount: 0 };
            }
          })
        );

        setResellers(resellersWithFollowers);

        if (username) {
          const result = await fetch('https://reviewhub-lc.onrender.com/api/auth/get-following', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username }),
          }).then((res) => res.json());
          setFollowingIds(result.followingResellers || []);
        }
      } catch (err) {
        console.error('Failed to fetch resellers or following data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchResellers();
  }, [username]);

  const displayedResellers = showOnlyFollowing
    ? resellers.filter((r) => followingIds.includes(r.resellerId))
    : resellers;

  const resellerIds = displayedResellers.map((r) => r.resellerId);

  const rows = Array(totalRows)
    .fill()
    .map((_, i) =>
      displayedResellers.slice(
        Math.floor((displayedResellers.length / totalRows) * i),
        Math.floor((displayedResellers.length / totalRows) * (i + 1))
      )
    );

  useEffect(() => {
    const initialIndices = {};
    for (let i = 0; i < totalRows; i++) {
      initialIndices[i] = 0;
    }
    setRowIndices(initialIndices);
  }, [displayedResellers]);

  const handleSelect = (event, value) => {
    setSelectedId(value);
    if (!value) {
      setDetails(null);
      return;
    }
    const selectedReseller = displayedResellers.find((r) => r.resellerId === value);
    setDetails(selectedReseller || null);
  };

  const handleRowPrev = (rowIdx) => {
    setRowIndices((prev) => ({
      ...prev,
      [rowIdx]: Math.max(prev[rowIdx] - 1, 0),
    }));
  };

  const handleRowNext = (rowIdx, rowLength) => {
    setRowIndices((prev) => ({
      ...prev,
      [rowIdx]: Math.min(prev[rowIdx] + 1, rowLength - cardsPerRowVisible),
    }));
  };

  const ResellerCard = ({ reseller }) => {
    const [isFollowing, setIsFollowing] = useState(false);

    useEffect(() => {
      if (username) {
        setIsFollowing(followingIds.map(String).includes(String(reseller.resellerId)));
      }
    }, [followingIds, reseller.resellerId]);

    const handleToggleFollow = () => {
      fetch('https://reviewhub-lc.onrender.com/api/auth/toggle-follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          resellerId: reseller.resellerId,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          const updatedFollowing = data.followingResellers || [];
          setFollowingIds(updatedFollowing);
          setIsFollowing(updatedFollowing.map(String).includes(String(reseller.resellerId)));
        });
    };

    const handleView = () => {
      navigate(`/reseller/${reseller.resellerId}`);
    };

    return (
     <Paper
  sx={{
    p: isMobile ? 0.5 : 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // ✅ translucent white
    backdropFilter: 'blur(6px)',                // ✅ optional glass blur effect
    borderRadius: 3,
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: isMobile ? 'auto' : 220,
    width: '100%',
    mx: isMobile ? 0 : 1,
    textAlign: isMobile ? 'left' : 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease-in-out',
    animation: 'fadeIn 0.5s ease',
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    },
  }}
  elevation={0} // ✅ remove background elevation color
>

      <Avatar
        alt={reseller.name}
        src={reseller.profileImageUrl || ''}
        sx={{
          width: isMobile ? 45 : 90,
          height: isMobile ? 45 : 90,
          border: '2px solid #444444',       // thicker pink border for visibility
          borderRadius: '50%',               // enforce circle
          backgroundColor: '#fff',           // in case image doesn't load
          objectFit: 'cover',                // ensure image fills avatar
          outline: 'none',
          boxShadow: 'none',
          '&:focus': { outline: 'none' },
        }}
      />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {reseller.resellerId}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            <strong>{reseller.followerCount || 0}</strong> Followers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: isMobile ? 0 : 1 }}>
          <Button variant="contained" size="small" onClick={handleView} sx={{    borderRadius: '20px',
                  backgroundColor: '#e6395f', }}>
            View
          </Button>
          {username && (
            <Button
             variant="contained"
              size="small"
              color={isFollowing ? 'secondary' : 'primary'}
              onClick={handleToggleFollow}
              sx={{
                minWidth: 80,
                height: 32,
                textTransform: 'none',
                    borderRadius: '20px',
                    
                  backgroundColor: '#e6395f',
              }}
            >
              {isFollowing ? 'following' : 'Follow'}
            </Button>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 1, px: 2 }}>
      <Box sx={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, pb: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
             color: '#e6395f',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            textAlign: 'center',
            mt: 2,
            mb: 3,
          }}
        >
          {showOnlyFollowing ? 'Your Followed Resellers' : 'Search Reseller by ID'}
        </Typography>

        {!showOnlyFollowing && (
          <Box sx={{ maxWidth: isMobile ? '100%' : 400, mx: 'auto', mb: 0 }}>
            <Autocomplete
              options={resellerIds}
              value={selectedId}
              onChange={handleSelect}
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isMobile ? 'Reseller ID' : 'Search by Reseller ID'}
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: '12px',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, gap: 2 }}>
          {[...Array(cardsPerRowVisible)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={isMobile ? 80 : 260}
              sx={{ borderRadius: 2 }}
            />
          ))}
          <CircularProgress color="primary" sx={{ ml: 2 }} />
        </Box>
      ) : details ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <ResellerCard reseller={details} />
        </Box>
      ) : displayedResellers.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          {showOnlyFollowing
            ? 'You are not following any resellers yet.'
            : 'No resellers found. Try searching with a different ID.'}
        </Typography>
      ) : isMobile ? (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {displayedResellers.map((reseller, idx) => (
            <ResellerCard key={idx} reseller={reseller} />
          ))}
        </Box>
      ) : (
        <Box sx={{ mt: 4 }}>
          {rows.map((row, rowIdx) => {
            if (row.length === 0) return null;
            const start = rowIndices[rowIdx] || 0;
            const end = start + cardsPerRowVisible;
            const visibleResellers = row.slice(start, end);

            return (
              <Box key={rowIdx} sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Button
                  onClick={() => handleRowPrev(rowIdx)}
                  disabled={start === 0}
                  sx={{
                    backgroundColor: '#ffffff',
                     color: '#e6395f',
                    border: '1px solid #e6395f',
                    minWidth: 44,
                    height: 44,
                    borderRadius: '50%',
                    mr: 2,
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </Button>

                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                  {visibleResellers.map((reseller, idx) => (
                    <Grid item xs={12 / cardsPerRowVisible} sm={12 / cardsPerRowVisible} key={idx}>
                      <ResellerCard reseller={reseller} />
                    </Grid>
                  ))}
                </Grid>

                <Button
                  onClick={() => handleRowNext(rowIdx, row.length)}
                  disabled={end >= row.length}
                  sx={{
                    backgroundColor: '#ffffff',
                color: '#e6395f',
                    border: '1px solid #e6395f',
                    minWidth: 40,
                    height: 44,
                    borderRadius: '50%',
                    ml: 2,
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </Button>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SearchById;
